import { useEffect, useRef, useState } from 'react';

/** True when the browser reports a match. Defensive because `matchMedia` is absent in some
 *  test environments and older embedded browsers, where "no match" is the safe reading. */
function prefers(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(query).matches;
}

const NO_HOVER = '(hover: none)';
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/**
 * Assembles the colonnade once, on a device that cannot hover.
 *
 * Hover drives assembly in pure CSS and needs nothing from JavaScript. Touch devices have no
 * hover, so without this the structure would sit at 20% opacity and scattered on the device
 * class that will be most of the traffic — reading as a rendering bug rather than a deliberate
 * composition.
 *
 * Three deliberate limits:
 *
 * - **It runs only where hovering is impossible.** On a pointer device it never observes
 *   anything, so it costs nothing there.
 * - **It fires once and disconnects.** Scrolling the structure out of view and back does not
 *   replay it; the handoff's "no idle or looping animation anywhere" is a requirement, not a
 *   preference (REQ-LANDING-004).
 * - **It does nothing under reduced motion.** The stylesheet already renders the structure
 *   fully assembled with no transition in that case, on every device class.
 *
 * Failure is biased towards assembled. This is decoration: a visitor who sees the structure
 * already built has lost nothing, whereas one who sees it permanently scattered thinks the page
 * is broken.
 */
export function useAssembleOnView<T extends HTMLElement>(): {
  ref: React.RefObject<T | null>;
  assembled: boolean;
} {
  const ref = useRef<T>(null);
  const [assembled, setAssembled] = useState(false);

  useEffect(() => {
    if (prefers(REDUCED_MOTION)) {
      return;
    }

    // Hover devices assemble in CSS. Nothing to do, and nothing to pay for.
    if (!prefers(NO_HOVER)) {
      return;
    }

    const element = ref.current;
    if (!element) {
      return;
    }

    if (typeof IntersectionObserver !== 'function') {
      setAssembled(true);
      return;
    }

    let observer: IntersectionObserver;
    try {
      observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) {
            return;
          }
          setAssembled(true);
          observer.disconnect();
        },
        // Enough of the structure has to be on screen for the assembly to be worth watching;
        // a single pixel at the edge of the viewport would spend the animation off-screen.
        { threshold: 0.25 },
      );
      observer.observe(element);
    } catch {
      setAssembled(true);
      return;
    }

    return () => { observer.disconnect(); };
  }, []);

  return { ref, assembled };
}
